import { FC } from 'react';
import {
  PageContentLayout,
  PageHeaderLayout,
  Loader,
  NothingFoundAlert,
  SideMenu,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { useRouteParams } from 'utils/useIdParam';
import { useTabsState } from 'utils/Tabs';
import { tabsSchema } from 'utils/schemas';
import { ROUTES } from 'config';
import {
  EmployeeGroupActions,
  EmployeesGroupBenefitsAssignments,
  EmployeesGroupEmployeesAssignments,
} from 'containers/employees-groups';
import { useEmployeeGroup } from 'store/employee-groups';
import { Stack } from '@mui/material';
import { useMe } from 'state/Administrators';

const employeeGroupDetailsTabsSchema = tabsSchema([
  'innerGroupAssignBenefits',
  'innerGroupAssignEmployees',
]);

export const EmployeeGroupDetails: FC = () => {
  const { id: employeeGroupId } = useRouteParams(ROUTES.employeeGroups.details);
  const { t } = useTranslation();
  const { hasViewPermission } = useMe();

  const { data: employeeGroup, isLoading } = useEmployeeGroup(employeeGroupId);

  const sideTabs = useTabsState(
    [
      ...(hasViewPermission(['benefits'])
        ? [
            {
              key: 'innerGroupAssignBenefits',
              label: t('sidebar.assignBenefits'),
            } as const,
          ]
        : []),
      ...(hasViewPermission(['employees'])
        ? [
            {
              key: 'innerGroupAssignEmployees',
              label: t('sidebar.assignEmployees'),
            } as const,
          ]
        : []),
    ],
    employeeGroupDetailsTabsSchema,
  );

  if (isLoading) return <Loader />;
  if (!employeeGroup) return <NothingFoundAlert />;

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('menu.groups'),
            url: ROUTES.employeeGroups.list,
          },
          {
            label: employeeGroup.name,
          },
        ]}
        side={
          !employeeGroup.isAllEmployeesGroup ? (
            <EmployeeGroupActions employeeGroup={employeeGroup} />
          ) : null
        }
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            activeTab={sideTabs.activeTab}
            onTabChange={sideTabs.goTo}
            tabs={sideTabs.tabs}
          />
        }
      >
        {sideTabs.activeTab === 'innerGroupAssignBenefits' &&
          hasViewPermission(['benefits']) && (
            <EmployeesGroupBenefitsAssignments />
          )}
        {sideTabs.activeTab === 'innerGroupAssignEmployees' &&
          hasViewPermission(['employees']) && (
            <EmployeesGroupEmployeesAssignments />
          )}
      </PageContentLayout>
    </Stack>
  );
};
